import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './app/App';
import { ErrorBoundary } from './app/ErrorBoundary';
import { initMetrics, screenOf } from './app/metrics';
import { createReporter, locationOf, report, setReporter, stackLocation } from './app/report';
import type { FetchFn } from './api/client';
import { deepLinkFromInitData } from './domain/deepLink';
import { webAddressRedirect } from './domain/webLogin';
import { consumeHandoff } from './api/webSession';
import { createBrowserApi } from './telegram/browser';
import { getMax, loadMaxSdk, maxLaunchData } from './telegram/max';
import { getTelegram } from './telegram/webapp';
import '@fontsource-variable/manrope';
import './ui/theme.css';
import './ui/web.css';

async function start() {
  // Max puts its signed launch data into the fragment; its bridge is loaded only then, Telegram never waits for it.
  const inMax = import.meta.env.MODE !== 'mock' && !getTelegram(window) && maxLaunchData(window) !== '';
  if (inMax) await loadMaxSdk();
  // A browser on tg. (the mini app address) moves to the web address first, fragment and all.
  const redirect = getTelegram(window) || inMax
    ? null
    : webAddressRedirect(location.origin, location.pathname, location.search, location.hash, import.meta.env.VITE_WEB_ORIGIN);
  if (redirect) {
    location.replace(redirect);
    return;
  }
  // First of all: a token handed over in the fragment leaves the address bar before anything can read or report it.
  const handoff = getTelegram(window) || inMax ? null : consumeHandoff(window);
  let fetchFn: FetchFn = (input, init) => fetch(input, init);
  if (import.meta.env.MODE === 'mock') {
    const mock = await import('./dev/mock');
    mock.installTelegramStub(window);
    fetchFn = mock.mockFetch;
  }

  const telegram = getTelegram(window) ?? (inMax || import.meta.env.MODE === 'mock' ? getMax(window) : null) ?? createBrowserApi(window);
  setReporter(
    createReporter({
      endpoint: `${import.meta.env.BASE_URL}e.gif`,
      version: __APP_VERSION__,
      platform: telegram.platform,
      tgVersion: telegram.version,
      screen: () => screenOf(window.location.pathname, import.meta.env.BASE_URL),
    }),
  );
  // Not awaited: the anonymous id may wait for CloudStorage (up to its timeout); events queue meanwhile.
  void initMetrics({
    endpoint: `${import.meta.env.BASE_URL}m.gif`,
    version: __APP_VERSION__,
    platform: telegram.platform,
    storage: telegram.storage,
    // The kind of deep link only (pass, request, news, object), never the id.
    source: telegram.messenger ? (deepLinkFromInitData(telegram.initData)?.kind ?? 'none') : 'browser',
    loadMs: typeof performance !== 'undefined' ? performance.now() : undefined,
  });
  window.addEventListener('error', (e) => report('js_error', e.message, locationOf(e.filename, e.lineno, e.colno)));
  window.addEventListener('unhandledrejection', (e) =>
    e.reason instanceof Error
      ? report('js_rejection', `${e.reason.name}: ${e.reason.message}`, stackLocation(e.reason.stack))
      : report('js_rejection'),
  );
  document.documentElement.dataset.theme = telegram.colorScheme;
  document.documentElement.dataset.platform = telegram.messenger ?? 'web';

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <ErrorBoundary onError={(error) => report('render_error', `${error.name}: ${error.message}`, stackLocation(error.stack))}>
        <App telegram={telegram} fetchFn={fetchFn} apiBaseUrl={import.meta.env.VITE_API_BASE_URL} handoff={handoff} />
      </ErrorBoundary>
    </StrictMode>,
  );
}

void start();
